import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components";
import { Switch, Text } from "@/ui";
import { useNotification } from "@/hooks";
import { useWorkingHours } from "@/hooks";
import { useScheduleContext } from "context";
import WorkingHourForm from "./WorkingHourForm";
import TimeSlotList from "./TimeSlotList";
import { WorkingHourProps, TimeInput } from "context/schedule/types";
import styles from "./WorkingHour.module.scss";

const WorkingHour: React.FC<WorkingHourProps> = ({ day }) => {
    const notify = useNotification();
    const {
        inputs,
        setInputs,
        disableTimeWindows,
        dayWithErrors,
        validateDayAndStore,
    } = useScheduleContext();

    const [customizeTimeSlot, setCustomizeTimeSlot] = useState(false);

    const {
        dayInputs,
        applyChangesAllDays,
        handleWorkingHourChange,
        handleTimeSlotChange,
        handleAddTimeSlot,
        handleRemoveTimeSlot,
        toggleCustomizeTimeSlot,
        applyChangeAllDays,
    } = useWorkingHours({
        day,
        inputs,
        setInputs,
        disableTimeWindows,
        notify,
    });

    useEffect(() => {
        validateDayAndStore(day, {
            working_hours: dayInputs.working_hours
                ? { ...dayInputs.working_hours, day_of_the_week: day }
                : null,
            time_slots: dayInputs.time_slots,
        });
    }, [day, dayInputs, validateDayAndStore]);

    const dayErrors = dayWithErrors?.[day] ?? {};

    const workingHourErrors = useMemo(
        () => ({
            ...(dayErrors?.start && { start: dayErrors.start }),
            ...(dayErrors?.end && { end: dayErrors.end }),
        }),
        [dayErrors],
    );

    const slotErrors = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(dayErrors ?? {}).filter(
                    ([k, v]) =>
                        (k === "general" && !!v) ||
                        (/^\d+$/.test(k) &&
                            v &&
                            typeof v === "object" &&
                            Object.keys(v).length > 0),
                ),
            ),
        [dayErrors],
    );

    const hasInvalidForDay = Object.keys(dayErrors || {}).length > 0;
    const hasWorkingPair =
        !!dayInputs?.working_hours?.start && !!dayInputs?.working_hours?.end;

    const handleCustomizeTimeSlot = (checked: boolean) => {
        if (checked && !customizeTimeSlot) {
            toggleCustomizeTimeSlot();
        }
        setCustomizeTimeSlot(checked);
    };

    const handleWorkingHourChangeWithValidation = (
        field: "start" | "end",
        value: string,
    ) => {
        handleWorkingHourChange(field, value);
        const next = {
            ...dayInputs,
            working_hours: dayInputs.working_hours
                ? {
                      ...dayInputs.working_hours,
                      [field]: value,
                      day_of_the_week: day,
                  }
                : null,
            time_slots: dayInputs.time_slots,
        };
        validateDayAndStore(day, next);
    };

    const handleAddTimeSlotWithValidation = () => {
        handleAddTimeSlot();
        validateDayAndStore(day, {
            working_hours: dayInputs.working_hours
                ? { ...dayInputs.working_hours, day_of_the_week: day }
                : null,
            time_slots: [...dayInputs.time_slots],
        });
    };

    const handleTimeSlotChangeWithValidation = (
        index: number,
        field: keyof TimeInput,
        value: any,
    ) => {
        handleTimeSlotChange(index, field, value);
        const next = {
            ...dayInputs,
            working_hours: dayInputs.working_hours
                ? { ...dayInputs.working_hours, day_of_the_week: day }
                : null,
            time_slots: dayInputs.time_slots.map((s, i) =>
                i === index ? { ...s, [field]: value } : s,
            ),
        };
        validateDayAndStore(day, next);
    };

    const handleRemoveTimeSlotWithValidation = (index: number) => {
        handleRemoveTimeSlot(index);
        const next = {
            ...dayInputs,
            working_hours: dayInputs.working_hours
                ? { ...dayInputs.working_hours, day_of_the_week: day }
                : null,
            time_slots: dayInputs.time_slots.filter((_, i) => i !== index),
        };
        validateDayAndStore(day, next);
    };

    return (
        <div className={styles.root}>
            <Switch
                checked={applyChangesAllDays === day || false}
                disabled={hasInvalidForDay || !hasWorkingPair}
                label="Apply this configuration to everyday"
                name="applyAllToday"
                onCheckedChange={() => applyChangeAllDays()}
            />

            {hasInvalidForDay ? (
                <p className={styles.errorText}>
                    Form contains errors. Fix them before applying to all days.
                </p>
            ) : null}

            <WorkingHourForm
                customizeTimeSlot={customizeTimeSlot}
                end={dayInputs.working_hours?.end}
                errors={workingHourErrors}
                onChange={handleWorkingHourChangeWithValidation}
                start={dayInputs.working_hours?.start}
                toggleCustomizeTimeSlot={toggleCustomizeTimeSlot}
            />

            {!disableTimeWindows ? (
                <>
                    <Switch
                        checked={!!customizeTimeSlot}
                        disabled={
                            !dayInputs.working_hours?.start &&
                            !dayInputs.working_hours?.end
                        }
                        label="Automatic generation of time slot"
                        name="showTimeSlot"
                        onCheckedChange={handleCustomizeTimeSlot}
                    />
                    <div className={styles.hint}>
                        <span className={styles.hintIcon}>
                            <Icon icon="info" />
                        </span>
                        <Text tone="secondary" variant="body-sm">
                            When this field is <strong>ACTIVE</strong>, the
                            system automatically generates one-hour intervals
                            that cover the store&apos;s working hours.
                            <br />
                            When this field is <strong>NOT ACTIVE</strong>, you
                            must manually define the time slots and ensure they
                            fully cover the store&apos;s working hours to avoid
                            issues with interval assignment.
                        </Text>
                    </div>

                    <TimeSlotList
                        active={inputs.active}
                        errors={slotErrors}
                        onAddSlot={handleAddTimeSlotWithValidation}
                        onChangeSlot={handleTimeSlotChangeWithValidation}
                        onRemoveSlot={handleRemoveTimeSlotWithValidation}
                        timeSlots={dayInputs.time_slots}
                        workingHour={dayInputs?.working_hours}
                    />
                </>
            ) : null}
        </div>
    );
};

export default WorkingHour;
